/**
 * Are two stored values the same? Equal as JSON, whatever order their
 * objects' keys come in. Arrays keep their order, which means something.
 *
 * Plain `JSON.stringify(a) === JSON.stringify(b)` is how this codebase used to
 * ask, and it is wrong for anything that has been through a database: the same
 * stored data comes back with object keys in different orders. Firestore's web
 * SDK returned a point as `{"z","y","x"}` to one read and `{"y","x","z"}` to
 * another (measured 2026-10-26), and Postgres `jsonb` reorders keys on every
 * write. Compared as text, an untouched value then reads as changed: the room
 * store rebuilt the scene for a snapshot that carried nothing new, and the
 * scanned-label rule could take a door whose `doorStyle` merely came back
 * reordered as edited.
 *
 * Plain stringify still decides the common cases: equal text is equal, and
 * reordering keys never changes the text's length, so a length mismatch is a
 * real difference. Only same-length text that differs is compared again with
 * every object's keys sorted. JSON's own rules apply throughout — `toJSON`
 * (a Date, a Firestore Timestamp), and a key holding `undefined` being the
 * same as no key.
 */

const withSortedKeys = (key, value) =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(
        Object.keys(value)
          .sort()
          .map((k) => [k, value[k]])
      )
    : value;

/**
 * @param {*} a
 * @param {*} b
 * @returns {boolean}
 */
function sameStoredValue(a, b) {
  if (a === b) return true;
  const textA = JSON.stringify(a);
  const textB = JSON.stringify(b);
  if (textA === textB) return true;
  if (textA === undefined || textB === undefined) return false;
  if (textA.length !== textB.length) return false;
  return (
    JSON.stringify(a, withSortedKeys) === JSON.stringify(b, withSortedKeys)
  );
}

module.exports = { sameStoredValue };
